import { List, Typography } from 'antd'
import { V3Transaction } from 'models/v3-transaction'

import { DateTimeV2 } from 'components/date-time'
import { AlignedContentRight } from 'components/wrapper'
import { useConnectorTranslation } from 'hooks/translation/useConnectorTranslation'
import { useTransactionMetricTranslation } from 'hooks/translation/useTransactionMetricTranslation'
import { useTransactionSearchTranslation } from 'hooks/translation/useTransactionSearchTranslation'
import { useCarTranslation } from 'hooks/translation/useCarTranslation'
import { useChargerTranslation } from 'hooks/translation/useChargerTranslation'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useListingBasicTrans } from 'hooks/translation/useListingBasicTrans'

import { renderChargingStop } from 'helpers/date-format'
import { formatDurationWithTwoDigits } from 'helpers/duration'
import { formatEnergy } from 'helpers/number-format'
import { theme } from 'theme'
import { useSelector } from 'hooks/useSelector'
import { useAppState } from 'state'
import configProvider from 'config'
import { formatStartMethodDisplay } from 'helpers/formatStartMethodDisplay'
import { renderFormatMessage } from 'helpers/intl'

interface props {
  transaction: V3Transaction
}

interface ListProps {
  property: string
  value: any
  key: string
  description?: any
  className?: string
}

const TransactionPanelItem: React.FC<props> = ({ transaction }) => {
  const { listingTitleText, listingIDText } = useListingBasicTrans()
  const { chargerTypeText, SNText, vendorText } = useChargerTranslation()
  const { connectorTypeText, connectorIdText } = useConnectorTranslation()

  const { brandText, modelText, socAfterChargingText, socBeforeChargingText, yearText } = useCarTranslation()
  const { totalEnergyText } = useTransactionMetricTranslation()
  const { ocppTransactionText } = useTransactionMetricTranslation()
  const { startMethodText, rfidNumberText, rfidNicknameText } = useTransactionSearchTranslation()
  const {
    connectedText,
    disconnectedText,
    chargingStartText,
    chargingEndText,
    chargingDurationText,
    tenantText,
  } = useGeneralTranslation()
  const { siteLabelCleaner } = useSelector()
  const { currentUser } = useAppState()
  const {
    listing,
    charger,
    user,
    connector,
    car,
    percentStart,
    percent,
    startMethod,
    rfidNumber,
    rfidNickName,
    startAt,
    endAt,
    tenant,
    chargeStartAt,
    chargeEndAt,
    timezone,
    currentChargingDuration,
    energy,
    ocppTransactionId
  } = transaction

  const ellipsisValue = (value: string): JSX.Element | string => {
    return (
      <Typography.Text style={{ color: theme.colors.blackOpacity45 }} ellipsis={{ tooltip: value }}>
        {value}
      </Typography.Text>
    )
  }

  const doesDisplayNameExist = siteLabelCleaner(tenant.combineName)

  const listingInfo = [
    {
      property: startMethodText,
      value: renderFormatMessage(
        `dashboard.transactionsPage.advancedSearch.startMethod.${startMethod.toLowerCase()}`,
        formatStartMethodDisplay(startMethod),
      ),
      key: 'startMethod',
      className: 'hideBorderBottom',
    },
    ...(currentUser?.role === 'admin' &&
      ['rfid', 'plug_and_charge', 'plug_and_play'].includes(startMethod.toLowerCase())
      ? [
          {
            property: rfidNickName ? rfidNicknameText : rfidNumberText,
            value: ellipsisValue(rfidNickName || rfidNumber || '-'),
            key: 'rfidNumber',
            className: 'hideBorderBottom ellipsis',
          },
        ]
      : []),
    {
      property: tenantText,
      value: ellipsisValue(doesDisplayNameExist),
      key: 'tenant',
      className: 'hideBorderBottom ellipsis',
    },
    ...((configProvider.config.network === 'itsElectric' && currentUser?.role === 'admin') ||
    configProvider.config.network !== 'itsElectric'
      ? [
          {
            property: 'Email',
            value: user && user.email ? ellipsisValue(user.email) : '-',
            key: 'email',
            className: 'hideBorderBottom ellipsis',
          },
        ]
      : []),
    { property: listingTitleText, value: listing.title, key: 'listingTitle', className: 'hideBorderBottom' },
    { property: listingIDText, value: listing.id, key: 'listingID' },
    ...(transaction.payterInfo
      ? [
          { property: "Credit Card Terminal:", key: 'payterCreditCardTerminal', className: 'hideBorderBottom' },
          { property: "Payter Device S/N", value: transaction.payterInfo.serialNumber, key: 'payterSerialNumber', className: 'hideBorderBottom' },
          { property: "Payter Txn ID", value: transaction.payterInfo.terminalTxnId, key: 'payterTerminalTxnId', className: 'hideBorderBottom' },
          { property: "Payter Session State", value: transaction.payterInfo.sessionState, key: 'payterSessionState', className: 'hideBorderBottom' },
          { property: "Payter Payment Method", value: transaction.payterInfo.paymentMethod, key: 'payterPaymentMethod' },
        ]
      : []),
  ]

  const timeDurationAndStateInfo = [
    {
      property: connectedText,
      value: <DateTimeV2 date={startAt} timezone={timezone} isSidePanel />,
      key: 'startAt',
      description: <br />,
      className: 'hideBorderBottom',
    },
    {
      property: disconnectedText,
      value: !endAt ? '-' : <DateTimeV2 date={endAt} timezone={timezone} isSidePanel />,
      key: 'endAt',
      description: !endAt ? null : <br />,
      className: 'hideBorderBottom',
    },
    {
      property: chargingStartText,
      value: !chargeStartAt ? '-' : <DateTimeV2 date={chargeStartAt} timezone={timezone} isSidePanel />,
      key: 'chargeStartAt',
      description: !chargeStartAt ? null : <br />,
      className: 'hideBorderBottom',
    },
    {
      property: chargingEndText,
      value: !chargeEndAt || chargeEndAt === null ? '-' : renderChargingStop(transaction as any),
      key: 'chargeEndAt',
      description: !chargeEndAt || chargeEndAt === null ? null : <br />,
      className: 'hideBorderBottom',
    },
    {
      property: chargingDurationText,
      value: currentChargingDuration === null || currentChargingDuration === undefined ? (
        '-'
      ) : (
        <AlignedContentRight children={formatDurationWithTwoDigits(currentChargingDuration)} />
      ),
      key: 'currentChargingDuration',
      className: 'hideBorderBottom',
    },
    {
      property: totalEnergyText,
      key: 'energy',
      value: energy === null || energy === undefined ? '-' : <AlignedContentRight children={`${formatEnergy(Math.max(0, energy) / 1000)} kWh`} />,
      className: 'hideBorderBottom',
    },
    ...(currentUser?.role === 'admin'
      ? [
          {
            property: ocppTransactionText,
            value: ocppTransactionId,
            key: 'ocppTransactionId',
            className: 'hideBorderBottom',
          },
        ]
      : []),
  ]

  const chargerInfo = [
    {
      property: vendorText,
      key: 'vendor',
      value: charger.chargePointVendor,
      className: 'hideBorderBottom',
    },
    {
      property: SNText,
      key: 'serialNumber',
      value: ellipsisValue(charger.chargePointSerialNumber),
      className: 'hideBorderBottom ellipsis',
    },
    {
      property: chargerTypeText,
      key: 'chargerType',
      value: charger.chargingType,
      className: 'hideBorderBottom',
    },
    {
      property: connectorTypeText,
      key: 'connectorType',
      value: connector.connectorType,
      className: 'hideBorderBottom',
    },
    { property: connectorIdText, key: 'connectorId', value: `#${connector.connectorId}` },
  ]

  const carInfo = [
    {
      property: brandText,
      key: 'make',
      value: car?.brand || '-',
      className: 'hideBorderBottom',
    },
    {
      property: `${modelText} & ${yearText}`,
      key: 'modelYear',
      value: car?.model && car.year ? ellipsisValue(`${car?.model} ${car?.year}`) : '-',
      className: 'hideBorderBottom ellipsis',
    },
    {
      property: socBeforeChargingText,
      key: 'socBeforeCharging',
      value: typeof percentStart === 'number' ? `${percentStart}%` : '-',
      className: 'hideBorderBottom',
    },
    { property: socAfterChargingText, key: 'socAfterCharging', value: typeof percent === 'number' ? `${percent}%` : '-' },
  ]

  const data = [...listingInfo, ...timeDurationAndStateInfo, ...chargerInfo, ...carInfo] as ListProps[]

  return (
    <>
      {data.map((t) => (
        <List.Item actions={[t.value]} className={t.className} key={`${transaction.id}-${t.key}`}>
          <List.Item.Meta title={t.property} description={t?.description} />
        </List.Item>
      ))}
    </>
  )
}

export default TransactionPanelItem

